// Robot status overlays, each one instanced draw for every robot on screen:
//  - Shells: faceted additive over-shells (shield = faint blue, hyper = magenta)
//  - Diamonds: the primed marker floating above the head
//  - Lasers: the sniper's red sight line (screen-width-stable, additive, unfogged)

import * as THREE from 'three';
import { buildDiamond } from './models';

// ---------------------------------------------------------------- shells
const SHELL_VERT = /* glsl */ `
attribute vec4 iCol;
varying vec4 vCol;
varying vec3 vView;
varying vec3 vObj;
void main() {
  vCol = iCol;
  vObj = position;
  vec4 mv = modelViewMatrix * instanceMatrix * vec4( position, 1.0 );
  vView = mv.xyz;
  gl_Position = projectionMatrix * mv;
}
`;
const SHELL_FRAG = /* glsl */ `
uniform float uTime;
varying vec4 vCol;
varying vec3 vView;
varying vec3 vObj;
void main() {
  vec3 n = normalize( cross( dFdx( vView ), dFdy( vView ) ) );
  float facing = abs( dot( n, normalize( vView ) ) );
  float fres = pow( 1.0 - facing, 2.0 );
  // per-facet print tone so it reads as plates, plus a slow scan band
  float facet = fract( sin( dot( floor( n * 3.0 ), vec3( 12.9898, 78.233, 37.719 ) ) ) * 43758.5453 );
  float scan = smoothstep( 0.0, 0.08, abs( fract( vObj.y * 0.9 - uTime * 0.35 ) - 0.5 ) - 0.42 );
  float a = vCol.a * ( 0.006 + 0.02 * facet + 0.75 * fres * fres * fres + 0.05 * ( 1.0 - scan ) );
  gl_FragColor = vec4( vCol.rgb * a, 1.0 );
}
`;

export class Shells {
  readonly mesh: THREE.InstancedMesh;
  private col: Float32Array;
  private aCol: THREE.InstancedBufferAttribute;
  private n = 0;
  readonly uTime = { value: 0 };
  constructor(private cap: number) {
    const geo = new THREE.IcosahedronGeometry(1, 1);
    this.col = new Float32Array(cap * 4);
    this.aCol = new THREE.InstancedBufferAttribute(this.col, 4).setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('iCol', this.aCol);
    const mat = new THREE.ShaderMaterial({
      vertexShader: SHELL_VERT, fragmentShader: SHELL_FRAG, uniforms: { uTime: this.uTime },
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.FrontSide,
    });
    this.mesh = new THREE.InstancedMesh(geo, mat, cap);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 2;
    this.mesh.count = 0; this.mesh.visible = false;
    this.mesh.name = 'robot-shells';
  }
  begin() { this.n = 0; }
  push(m: THREE.Matrix4, c: THREE.Color, a: number) {
    if (this.n >= this.cap) return;
    m.toArray(this.mesh.instanceMatrix.array as Float32Array, this.n * 16);
    const o = this.n * 4;
    this.col[o] = c.r; this.col[o + 1] = c.g; this.col[o + 2] = c.b; this.col[o + 3] = a;
    this.n++;
  }
  end(time: number) {
    this.uTime.value = time;
    const n = this.n;
    this.mesh.count = n; this.mesh.visible = n > 0;
    if (!n) return;
    const im = this.mesh.instanceMatrix;
    im.clearUpdateRanges(); im.addUpdateRange(0, n * 16); im.needsUpdate = true;
    this.aCol.clearUpdateRanges(); this.aCol.addUpdateRange(0, n * 4); this.aCol.needsUpdate = true;
  }
}

// ---------------------------------------------------------------- primed diamonds
export class Diamonds {
  readonly mesh: THREE.InstancedMesh;
  private n = 0;
  constructor(private cap: number) {
    const geo = buildDiamond();
    const mat = new THREE.MeshBasicMaterial({ vertexColors: true });
    this.mesh = new THREE.InstancedMesh(geo, mat, cap);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.setColorAt(0, new THREE.Color(1, 1, 1));
    this.mesh.instanceColor!.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0; this.mesh.visible = false;
    this.mesh.name = 'robot-prime-diamonds';
  }
  begin() { this.n = 0; }
  push(m: THREE.Matrix4, c: THREE.Color) {
    if (this.n >= this.cap) return;
    this.mesh.setMatrixAt(this.n, m);
    this.mesh.setColorAt(this.n, c);
    this.n++;
  }
  end() {
    const n = this.n;
    this.mesh.count = n; this.mesh.visible = n > 0;
    if (!n) return;
    const im = this.mesh.instanceMatrix, ic = this.mesh.instanceColor!;
    im.clearUpdateRanges(); im.addUpdateRange(0, n * 16); im.needsUpdate = true;
    ic.clearUpdateRanges(); ic.addUpdateRange(0, n * 3); ic.needsUpdate = true;
  }
}

// ---------------------------------------------------------------- sniper lasers
const LASER_VERT = /* glsl */ `
attribute vec3 iA;
attribute vec3 iB;
attribute vec2 iK;
varying float vS;
varying float vT;
varying float vK;
varying float vLen;
void main() {
  vec3 p = mix( iA, iB, position.y );
  vec3 d = iB - iA;
  vLen = length( d );
  vec3 dir = d / max( vLen, 1e-4 );
  vec3 toCam = cameraPosition - p;
  float dist = length( toCam );
  vec3 side = normalize( cross( dir, toCam ) );
  // roughly constant on-screen width: ~2.5 px core at any range, wider soft glow
  float w = 0.012 + dist * 0.0032;
  p += side * position.x * w;
  vS = position.x; vT = position.y; vK = iK.x;
  gl_Position = projectionMatrix * viewMatrix * vec4( p, 1.0 );
}
`;
const LASER_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uTime;
varying float vS;
varying float vT;
varying float vK;
varying float vLen;
void main() {
  float s = abs( vS );
  float core = smoothstep( 0.32, 0.0, s );
  float glow = exp( -s * 3.2 ) * 0.55;
  // a pulse crawling toward the target
  float pulse = 0.75 + 0.25 * sin( vT * vLen * 1.4 - uTime * 30.0 );
  float fadeIn = smoothstep( 0.0, 0.015, vT );
  vec3 col = uColor * glow * pulse * 1.4 + mix( uColor, vec3( 1.0, 0.75, 0.6 ), 0.22 ) * core;
  gl_FragColor = vec4( col * vK * fadeIn * 1.6, 1.0 );
}
`;

export class Lasers {
  readonly mesh: THREE.Mesh;
  private geo: THREE.InstancedBufferGeometry;
  private a: Float32Array; private b: Float32Array; private k: Float32Array;
  private aA: THREE.InstancedBufferAttribute; private aB: THREE.InstancedBufferAttribute; private aK: THREE.InstancedBufferAttribute;
  private n = 0;
  readonly uTime = { value: 0 };
  constructor(private cap: number, color: THREE.ColorRepresentation) {
    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([-1, 0, 0, 1, 0, 0, 1, 1, 0, -1, 1, 0], 3));
    g.setIndex([0, 1, 2, 0, 2, 3]);
    this.a = new Float32Array(cap * 3); this.b = new Float32Array(cap * 3); this.k = new Float32Array(cap * 2);
    this.aA = new THREE.InstancedBufferAttribute(this.a, 3).setUsage(THREE.DynamicDrawUsage);
    this.aB = new THREE.InstancedBufferAttribute(this.b, 3).setUsage(THREE.DynamicDrawUsage);
    this.aK = new THREE.InstancedBufferAttribute(this.k, 2).setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('iA', this.aA); g.setAttribute('iB', this.aB); g.setAttribute('iK', this.aK);
    g.instanceCount = 0;
    this.geo = g;
    const mat = new THREE.ShaderMaterial({
      vertexShader: LASER_VERT, fragmentShader: LASER_FRAG,
      uniforms: { uColor: { value: new THREE.Color(color) }, uTime: this.uTime },
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
    });
    this.mesh = new THREE.Mesh(g, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 3;
    this.mesh.visible = false;
    this.mesh.name = 'robot-lasers';
  }
  begin() { this.n = 0; }
  push(a: THREE.Vector3, b: THREE.Vector3, k: number) {
    if (this.n >= this.cap) return;
    const i = this.n;
    this.a[i * 3] = a.x; this.a[i * 3 + 1] = a.y; this.a[i * 3 + 2] = a.z;
    this.b[i * 3] = b.x; this.b[i * 3 + 1] = b.y; this.b[i * 3 + 2] = b.z;
    this.k[i * 2] = k; this.k[i * 2 + 1] = 0;
    this.n++;
  }
  end(time: number) {
    this.uTime.value = time;
    const n = this.n;
    this.geo.instanceCount = n;
    this.mesh.visible = n > 0;
    if (!n) return;
    for (const [at, w] of [[this.aA, 3], [this.aB, 3], [this.aK, 2]] as const) {
      at.clearUpdateRanges(); at.addUpdateRange(0, n * w); at.needsUpdate = true;
    }
  }
}

// ---------------------------------------------------------------- contact blobs
// A soft dark pool under every robot so it sits ON the ground at 30 m (the sun shadow alone
// gets lost in the grass and haze). Multiply-blended warm dark, no depth write, one draw.
const BLOB_VERT = /* glsl */ `
attribute float iA;
varying vec2 vP;
varying float vA;
void main() {
  vP = position.xz * 2.0; vA = iA;
  // pulled toward the camera along its own view ray (no screen shift) so it darkens the grass
  // that hides the feet (blades 15-20 m out cover a 20 m robot's ground) instead of hiding under
  // it; the pull is a fifth of the range, so a player or robot well in front still covers it
  vec4 mv = modelViewMatrix * instanceMatrix * vec4( position, 1.0 );
  float d = length( mv.xyz );
  mv.xyz *= max( d - min( 4.0, d * 0.22 ), 0.05 ) / d;
  gl_Position = projectionMatrix * mv;
}
`;
const BLOB_FRAG = /* glsl */ `
varying vec2 vP;
varying float vA;
void main() {
  float r = length( vP );
  float a = ( 1.0 - smoothstep( 0.35, 1.0, r ) ) * vA;
  if ( a < 0.004 ) discard;
  // multiplied over the ground: darkens it and keeps its hue, leaning warm (no grey film)
  gl_FragColor = vec4( mix( vec3( 1.0 ), vec3( 0.34, 0.24, 0.15 ), a ), 1.0 );
}
`;
export class Blobs {
  readonly mesh: THREE.InstancedMesh;
  private al: Float32Array;
  private aA: THREE.InstancedBufferAttribute;
  private n = 0;
  constructor(private cap: number) {
    const geo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.al = new Float32Array(cap);
    this.aA = new THREE.InstancedBufferAttribute(this.al, 1).setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('iA', this.aA);
    const mat = new THREE.ShaderMaterial({
      vertexShader: BLOB_VERT, fragmentShader: BLOB_FRAG,
      transparent: true, depthWrite: false, side: THREE.DoubleSide,
      blending: THREE.CustomBlending, blendEquation: THREE.AddEquation, blendSrc: THREE.ZeroFactor, blendDst: THREE.SrcColorFactor,
    });
    this.mesh = new THREE.InstancedMesh(geo, mat, cap);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 2; // after the ground, which may itself sit in the transparent pass
    this.mesh.count = 0; this.mesh.visible = false;
    this.mesh.name = 'robot-contact-blobs';
  }
  begin() { this.n = 0; }
  /** a blob at (x, y, z), sx by sz across (yaw-aligned), opacity a */
  push(x: number, y: number, z: number, yaw: number, sx: number, sz: number, a: number) {
    if (this.n >= this.cap) return;
    const m = this.mesh.instanceMatrix.array as Float32Array, o = this.n * 16;
    const c = Math.cos(yaw), s = Math.sin(yaw);
    m[o] = c * sx; m[o + 1] = 0; m[o + 2] = -s * sx; m[o + 3] = 0;
    m[o + 4] = 0; m[o + 5] = 1; m[o + 6] = 0; m[o + 7] = 0;
    m[o + 8] = s * sz; m[o + 9] = 0; m[o + 10] = c * sz; m[o + 11] = 0;
    m[o + 12] = x; m[o + 13] = y; m[o + 14] = z; m[o + 15] = 1;
    this.al[this.n] = a;
    this.n++;
  }
  end() {
    const n = this.n;
    this.mesh.count = n; this.mesh.visible = n > 0;
    if (!n) return;
    const im = this.mesh.instanceMatrix;
    im.clearUpdateRanges(); im.addUpdateRange(0, n * 16); im.needsUpdate = true;
    this.aA.clearUpdateRanges(); this.aA.addUpdateRange(0, n); this.aA.needsUpdate = true;
  }
}
